import { formatGifAltThreadPreview } from './message-media-display'
import { parseImagesMessageBody } from './message-images'
import { callSystemThreadPreview } from '@shared/calls/call-system-message'
import { liveLocationThreadPreview } from '@shared/messaging/live-location-message'
import {
  formatAttachmentTypeLabel,
  formatImagesSentLabel,
} from '@shared/messaging/media-message-preview'

/** Machine-readable reply prefix embedded in message body (no schema field yet). */
const REPLY_MARKER = /^<!--ascend-reply:(.+?)-->\n?/

export type ReplyQuote = {
  messageId: string
  senderName: string
  text: string
}

export function encodeReplyBody(quote: ReplyQuote, body: string): string {
  const payload = JSON.stringify({
    messageId: quote.messageId,
    senderName: quote.senderName,
    text: quote.text.slice(0, 200),
  })
  return `<!--ascend-reply:${payload}-->\n${body}`
}

export function parseReplyBody(body: string): { quote?: ReplyQuote; text: string } {
  const match = body.match(REPLY_MARKER)
  if (!match) return { text: body.trim() }
  try {
    const parsed = JSON.parse(match[1]) as ReplyQuote
    if (parsed.messageId && parsed.senderName != null) {
      return { quote: parsed, text: body.slice(match[0].length).trim() }
    }
  } catch {
    /* fall through */
  }
  return { text: body.trim() }
}

export function truncateQuote(text: string, max = 80): string {
  const trimmed = text.trim()
  if (trimmed.length <= max) return trimmed
  return `${trimmed.slice(0, max)}…`
}

/** Human-readable sidebar / thread-list preview (strips reply metadata). */
export function formatThreadPreview(body: string, max = 120): string {
  const trimmed = body?.trim() ?? ''
  if (!trimmed) return ''

  // Call SYSTEM messages (e.g. callback requests, call ended/missed) store an
  // encoded body; render a friendly label instead of leaking raw ASCEND_CALL JSON.
  const callPreview = callSystemThreadPreview(trimmed)
  if (callPreview) return truncateQuote(callPreview, max)

  const livePreview = liveLocationThreadPreview(trimmed)
  if (livePreview) return truncateQuote(livePreview, max)

  const { quote, text: afterReply } = parseReplyBody(trimmed)
  const { extraUrls, visibleText: text } = parseImagesMessageBody(afterReply)

  if (quote) {
    const snippet = truncateQuote(text || quote.text, max - 13)
    return snippet ? `Replied to: ${snippet}` : 'Reply'
  }

  if (extraUrls.length > 0) {
    const label = formatImagesSentLabel(extraUrls.length + 1)
    return text ? truncateQuote(`${label} · ${text}`, max) : label
  }

  // The server stores a length-capped `lastMessagePreview`; for an image
  // message that cap can slice off the closing `-->`, leaving an unclosed
  // marker (e.g. `<!--ascend-images:["https://…`) that the parser above can't
  // strip. Detect the raw marker prefix and show a clean label instead of
  // leaking the JSON into the conversation list.
  if (text.startsWith('<!--ascend-images:')) {
    return formatImagesSentLabel(null)
  }
  if (text.startsWith('<!--ascend-gif-alt:')) {
    return formatAttachmentTypeLabel('GIF')
  }

  // Empty-body media messages are stored server-side as `[IMAGE]`, `[GIF]`,
  // etc. — translate the placeholder to a friendly "Sent an image" label.
  const bracketMatch = text.match(/^\[(IMAGE|GIF|VIDEO|VOICE|FILE)\]$/)
  if (bracketMatch) {
    return formatAttachmentTypeLabel(bracketMatch[1])
  }

  const gifPreview = formatGifAltThreadPreview(text)
  if (gifPreview) return gifPreview

  return truncateQuote(text, max)
}
